import { FormEventHandler, useMemo } from 'react'
import { Document } from '../../shared/types'
import { Button } from '../components/button'
import { useFormLayout } from '../layouts'
import { FormLayout } from '../layouts/form-layout'
import { FormLayout as FormLayoutType, LayoutElement } from '../layouts/types'

type Props = {
	data?: Document
	onSubmit: FormEventHandler
	isPending?: boolean
}

export function ReservationDocumentForm({ data, onSubmit, isPending }: Props) {
	const { layout } = useFormLayout('document')
	const normalizedLayout = useMemo(() => ensureDocumentUploadField(layout), [layout])

	return (
		<form
			className="space-y-4 m-auto max-w-[60em]"
			onSubmit={onSubmit}
			encType="multipart/form-data"
		>
			<h3 className="text-md font-bold">{data ? 'Editace dokumentu' : 'Nový dokument'}</h3>
			<FormLayout layout={normalizedLayout} data={data} />
			<Button type="submit" isPending={isPending} isDisabled={isPending}>
				Uložit
			</Button>
		</form>
	)
}

function ensureDocumentUploadField(layout: FormLayoutType): FormLayoutType {
	const withForcedType = forceContentType(layout)
	if (hasContentField(withForcedType)) {
		return withForcedType
	}

	// Keep document form usable even when DB layout misses the upload field.
	return [
		...withForcedType,
		{
			identifier: 'content',
			name: 'Soubor',
			type: 'file',
			isEditable: false,
		},
	]
}

function hasContentField(layout: FormLayoutType): boolean {
	for (const element of layout) {
		if (isFieldElement(element) && element.identifier === 'content') {
			return true
		}
		if (element.type === 'section' && hasContentField(element.components)) {
			return true
		}
		if (element.type === 'condition') {
			if (
				element.conditions.some(condition => hasContentField(condition.then)) ||
				(element.else ? hasContentField(element.else) : false)
			) {
				return true
			}
		}
	}
	return false
}

function forceContentType(layout: FormLayoutType): FormLayoutType {
	return layout.map(element => {
		if (isFieldElement(element) && element.identifier === 'content') {
			return {
				...element,
				type: 'file',
				isEditable: false,
			}
		}
		if (element.type === 'section') {
			return {
				...element,
				components: forceContentType(element.components),
			}
		}
		if (element.type === 'condition') {
			return {
				...element,
				conditions: element.conditions.map(condition => ({
					...condition,
					then: forceContentType(condition.then),
				})),
				else: element.else ? forceContentType(element.else) : undefined,
			}
		}
		return element
	})
}

function isFieldElement(
	element: LayoutElement,
): element is Extract<LayoutElement, { identifier: string }> {
	return Object.hasOwn(element, 'identifier')
}
